import { TelemetryData } from '@cf-shared/telemetry-shared';
import { sleep } from '@cf-shared/shared-util';
import { FlushTelemetryParamsRegistered, FlushTelemetryResults } from '@cf-shared/flush-telemetry-interface';
import { AppEnvironment } from 'environments/environment';
import { isCapacitorNative } from 'environments/environment-helpers';
import { getUnversionedRelayOrigin } from 'util/backend-origin';
import { base64EncodeUtf8String } from './browser-utils';
import { LoggingService, isTransientFetchError } from './logging.service';
import { createTimeoutSignal, isTimeoutError } from './timeout-signal';
import { ApiFetchWindow, pickApiFetchImpl } from './cloud-functions-retry';
import { CloudFunctionsService } from './cloud-functions.service';
import { UserDb } from './user-db.service';
import { TelemetryQueue } from './telemetry-service-queue';
import { isDatabaseUnavailable } from './database-unavailable-error';
import { ConnectionState, OnlineStateService } from './online-state.service';

// #4762: how long a single send to Mixpanel may take before we give up on it. Generous,
// because this is background work that nobody is waiting on and a slow network is normal —
// the point is only that it ENDS. Compare the 80 s the cloud-functions path allows a call a
// user really is waiting for. What this number protects is not the individual send but the
// shared background flusher, which cannot move on until this call returns.
const MIXPANEL_SEND_TIMEOUT_MS = 20_000;

// params for exponential backoff when we need to retry sending telemetry events
const exponentialBackoffMin_sec = 5;
const exponentialBackoffMax_sec = 60 * 5;
const exponentialBackoffFactor = 2;

/*
How a telemetry payload actually REACHES Mixpanel. TelemetryService decides what to report
and with what properties; this class owns everything after that decision — the five-day
delegation cut-off, the direct relay POST and its bound, the offline branch, the ad-blocker
fallback, the exponential backoff, and the server-side flush.

Constructed by TelemetryService, exactly like TelemetryQueue: a plain class rather than an
Angular injectable, handed its collaborators by the service. The user database arrives later
via initialize, because it does not exist when the service is constructed.
*/
export class TelemetryTransport {
  constructor(
    private queue: TelemetryQueue,
    private onlineStateService: OnlineStateService,
    private cloudFunctions: CloudFunctionsService,
    private logging: LoggingService,
  ) {
  }

  private userDb: UserDb;
  // whether nobody is signed in, by the app's own login state (AuthService.user). the
  // server-side flush needs a signed-in user, see backgroundFlusherAsync
  private isSignedOut: () => boolean = () => false;

  initialize(userDb: UserDb, isSignedOut: () => boolean) {
    this.userDb = userDb;
    this.isSignedOut = isSignedOut;
  }

  // Whether our own events may be sent at all. Deliberately READ from the queue rather than
  // held as a copy of its own: TelemetryService.setAnalyticsEnabled is the only writer and
  // already sets its own field and the queue's together, so a third copy here would be one
  // more thing to forget — and forgetting it would compile perfectly and silently stop all
  // sending, with nothing reporting it.
  private get analyticsDisabled(): boolean {
    return this.queue.analyticsDisabled;
  }

  async postEncodedRequest(url: string, body: any): Promise<{ status: number, result: string }> {
    // this trick will convert the object to a x-www-form-urlencoded string, while handling UTF8 characters correctly
    let encodedBody = `data=${base64EncodeUtf8String(JSON.stringify(body))}`;

    // #4762: BOUNDED. The background flusher cannot decide whether to retry until this call
    // returns, so one request that never answers wedges the whole flusher for the rest of the
    // session — see design-docs/boot-sequence.md and design-docs/telemetry.md.
    //
    // The fetch implementation is CHOSEN rather than taken from the global, and that is what
    // makes the bound real on a phone. CapacitorHttp is enabled (client/capacitor.config.ts),
    // which replaces window.fetch on iOS and Android with a native-HTTP patch that IGNORES the
    // JS AbortSignal — so a signal passed to the global fetch bounds the web build and does
    // nothing at all on native. pickApiFetchImpl hands back the browser's original fetch there
    // (#4038 built it for exactly this), so the signal is honoured on every platform.
    //
    // Safe for this endpoint specifically: the relay is our own /api/t/ path, the server's CORS
    // allow-lists both native WebView origins, and this POST carries no cookies and no auth
    // header — so nothing the native-HTTP patch exists to provide is needed here. And unlike
    // the cloud-functions call site there is no report when the saved original is missing:
    // this runs per telemetry event, so it would fire continuously.
    //
    // A timed-out send is not a lost event — the caller treats it as "could not send", which
    // when online hands the event to the server-side queue, the same path an ad-blocker block
    // takes. The signal covers reading the body too, so a stalled mid-stream response is
    // bounded as well.
    const sendFetch = pickApiFetchImpl(true, isCapacitorNative(), window as unknown as ApiFetchWindow);
    const response = await sendFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: encodedBody,
      signal: createTimeoutSignal(MIXPANEL_SEND_TIMEOUT_MS),
    });

    const text = await response.text();
    return { status: response.status, result: text };
  }

  // this function (and its internals) deals just with making sure the background flusher is not double-started
  kickoffBackgroundFlusher() {
    void this.kickoffBackgroundFlusher_internalAsync();
  }

  /** Flush telemetry and wait for completion. Returns a promise that resolves when flushing is done.
   *  If a flusher is already in progress, waits for it to finish instead of returning immediately. */
  async flushAsync(): Promise<void> {
    // if a flusher is already running, wait for it instead of no-oping
    if (this.activeFlusherPromise) {
      await this.activeFlusherPromise;
      return;
    }
    await this.kickoffBackgroundFlusher_internalAsync();
  }

  flusherActive = false;
  private activeFlusherPromise: Promise<void> | null = null;
  async kickoffBackgroundFlusher_internalAsync() {

    // if the telemetry is disabled, we are done (this keeps objects in the memory queue until cookies are enabled)
    if (this.analyticsDisabled) {
      console.log('Telemetry: Data sending skipped because analytics are disabled');
      return;
    }

    if (this.flusherActive) {
      return;
    }

    this.flusherActive = true;
    console.log('Telemetry: background flusher started');

    // #6232: the reset sits in a finally. when it came after a plain await, one throw left
    // flusherActive true for the rest of the page session, so no telemetry was sent again and
    // every later flushAsync() rejected at once with the old error
    this.activeFlusherPromise = this.runFlusherReportingFailuresAsync();
    try {
      await this.activeFlusherPromise;
    } finally {
      this.activeFlusherPromise = null;
      this.flusherActive = false;
      console.log('Telemetry: background flusher stopped');
    }
  }

  // the promise flushAsync() and the kickoff share never rejects: the flusher is best-effort
  // background work, and the kickoff drops its promise, so a rejection would also be unhandled.
  // every failure the flusher expects is already answered inside it, so a throw reaching here
  // is a bug and is reported once
  private async runFlusherReportingFailuresAsync(): Promise<void> {
    try {
      await this.retryFlushWithExponentialBackoffAsync();
    } catch (err) {
      this.logging.trackSilentProblem('TelemetryFlusherFailed', { error: err });
    }
  }

  // this function deals just with the doing exporential backoff retries when needed
  // (this is only needed for the server-side) flushing
  async retryFlushWithExponentialBackoffAsync(): Promise<void> {
    let numAttempts = 0;
    for (; ;) {
      let { result, numLocallySent, numDelegated } = await this.backgroundFlusherAsync();
      console.log(`Telemetry: background flusher, ${numLocallySent} locally sent, ${numDelegated} delegated, result: ${result}`);
      if (result === 'done') {
        break;
      }
      console.assert(result === 'backoff');
      numAttempts++;
      const delay = Math.min(Math.pow(exponentialBackoffFactor, numAttempts) * exponentialBackoffMin_sec, exponentialBackoffMax_sec);
      console.log(`Telemetry: background flusher, backoff attempt ${numAttempts} will retry in ${delay} seconds`);
      await sleep(delay * 1000);
    }
  }


  /*
  Any telemetry data is just added to the in-memory queue and then this function handles all the different ways
  it can be delivered to MixPanel:
  - events older than 5 days are immediately delegated to server-side processing (Mixpanel client limit)
  - if possible, recent events are sent to MixPanel directly
  - if not possible, it is saved to the local database - from where it will be auto-synced to the remote database
    (it could be not possible simply because the user is offline - or because the user is using an ad-blocker)
  - if it was because the user was offline, it will be retried in the background, or at the next start
  - if it was due to adblocker we save it separately, and when accumulated, we will ask the server to flush it for us

  - the in-memory queue, localStorage queue, and disk queue all hold the same data
    - the in-memory queue is the only one that is actively being processed
    - the localStorage queue is just a backup in case the in-memory queue is lost
    - the disk queue is the one that is being synced to the server in case the session is lost, etc
    - a separate server-side queue (different id prefix) is for the events that we suspect are being blocked by adblockers
  */
  async backgroundFlusherAsync(): Promise<{ result: 'done' | 'backoff', numLocallySent: number, numDelegated: number }> {

    // while there's data in the queue
    let data: TelemetryData;
    let numLocallySent = 0;
    let numDelegated = 0;
    const fiveDaysInMs = 5 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    for (; ;) {
      data = this.queue.peekOldestData();
      if (!data) {
        break;
      }

      // Check if event is older than 5 days (Mixpanel client ingestion limit)
      let eventTimestamp: number;
      if ('properties' in data && data.properties.time) {
        eventTimestamp = data.properties.time;
      } else {
        // For user updates, extract timestamp from ID if possible
        const match = data._id.match(/\/(\d+)-/);
        eventTimestamp = match ? parseInt(match[1]) : now;
      }

      const isOldEvent = (now - eventTimestamp) > fiveDaysInMs;

      // Skip Mixpanel for old events and delegate directly to server
      if (isOldEvent) {
        console.log(`Telemetry: Event older than 5 days, delegating to server: ${data._id}`);
        // if the server-side copy could not be written, the event is still at the head of the
        // queue: stop and let the backoff retry it later, instead of spinning on it
        if (!await this.queue.delegateDataToServerSideAsync(data)) {
          return { result: 'backoff', numLocallySent, numDelegated };
        }
        numDelegated++;
        continue;
      }

      // if it can be received by MixPanel (online, no adblockers...)
      if (await this.callMixPanelAsync(data)) {
        numLocallySent++;
        // remove it from the queue (this will internally delete from the disk, etc)
        await this.queue.removeDataAsync(data);
        // go to the next in queue
        continue;
      }
      // if sending to mixpanel failed for whatever reason...

      // if we are offline
      if (this.onlineStateService.connectionState === ConnectionState.Offline) {
        // make sure the entire queue is saved to disk
        let { numSaved } = await this.queue.saveEntireQueueToDiskAsync();
        console.log(`Telemetry: background flusher - user is offline, ${numSaved} events saved for later`);
        // we are done
        return { result: 'done', numLocallySent, numDelegated };
      }

      // if we got here, it means that we are online, but we couldn't send the data to MixPanel...
      // ...so it is likely there's an adblocker in the way

      // leave this piece of data to be handled on the server side (this removes it from the queue, so we can proceed to the next one)
      // if the server-side copy could not be written, the event is still at the head of the
      // queue: stop and let the backoff retry it later, instead of spinning on it
      if (!await this.queue.delegateDataToServerSideAsync(data)) {
        return { result: 'backoff', numLocallySent, numDelegated };
      }
      numDelegated++;
    }

    // at this point the queue is empty, now check if there's something that should be processed by the server.
    // ANY backlog triggers a flush. this used to be "> 10" as a batching economy, but that
    // stranded small backlogs forever: a user whose direct sends failed during their only
    // session (June 2026 iOS engage%23 incident, see design-docs/telemetry.md) delegated a
    // handful of profile updates, never crossed 10, never returned — so the docs sat in
    // their couch db and the user silently dropped out of the lifecycle-email flows. a
    // flush call for a small backlog is cheap and rare (delegation only happens on send
    // failure), so drain everything as soon as it exists.
    let serverSideCount: number | false;
    try {
      serverSideCount = this.userDb && this.userDb.isInitialized() && await this.userDb.getTelemetryDataServerSideCountAsync();
    } catch (err) {
      // if the database could not answer — a recovery cut the read short, or it is being torn
      // down at sign-out (#6232) — this run is done. the recovery service or our own sign-out
      // owns that failure, so nothing is reported here. it is 'done' rather than 'backoff'
      // because the first backoff sleep is 10 s, and a sign-out flushAsync() waiting on that
      // sleep would hit its own timeout. nothing is lost: the delegated documents stay in the
      // database and the next kickoff reads the count again
      if (isDatabaseUnavailable(err)) {
        console.log(`Telemetry: server-side count skipped (${err.reason})`);
        return { result: 'done', numLocallySent, numDelegated };
      }
      throw err;
    }
    // if nobody is signed in, the flush call cannot succeed: it needs the user's token and
    // fails at once without one. at sign-out the user is always signed out before the
    // teardown's flushAsync() runs, so a backoff here only made sign-out wait out its own
    // timeout (df-ff409f8a9f93). this run is done; the delegated documents stay in the
    // database and the next kickoff after a login reads the count again. the signal is the
    // app's own login state on purpose: a CloudFunctionAuthError alone also happens on a warm
    // resume for a user who IS signed in, and there the backoff retry can succeed
    if (serverSideCount > 0 && this.isSignedOut()) {
      console.log(`Telemetry: server-side flush skipped, nobody is signed in (${serverSideCount} events wait)`);
      return { result: 'done', numLocallySent, numDelegated };
    }
    if (serverSideCount > 0) {
      // catch HTTP non-2xx + network-exhaustion so the flusher returns 'backoff'
      // instead of an unhandled rejection on activeFlusherPromise
      let result: FlushTelemetryResults | null = null;
      let flushError: unknown;
      try {
        result = await this.cloudFunctions.flushTelemetry({} as FlushTelemetryParamsRegistered);
      } catch (err) {
        flushError = err;
      }
      if (result && result.allOk) {
        console.log(`Telemetry: server-side flush successful (${serverSideCount} events)`);
      } else {
        console.error('Telemetry: server-side flush failed', flushError ?? '');
        // if the flush failed, we will retry later
        return { result: 'backoff', numLocallySent, numDelegated };
      }
    }

    // when all data is processed, we are done
    return { result: 'done', numLocallySent, numDelegated };
  }

  // returns true if event successfully sent
  async callMixPanelAsync(data: TelemetryData): Promise<boolean> {
    // don't even try to send if offline, or if telemetry is disabled
    if (this.onlineStateService.connectionState === ConnectionState.Offline || this.analyticsDisabled) {
      return false;
    }

    // send a POST request to MixPanel (via proxy to bypass ad-blockers).
    // Same relay accessor as the SDK init in telemetry.service.ts (#4405), but note the
    // two are not guaranteed to pick the SAME host: the SDK captures its api_host once
    // in the constructor, while this one is recomputed per send. So on a
    // first-ever native boot the SDK can stay pinned to apex for the session
    // while a later send here uses canary. That is harmless, and it is the
    // reason neither has to wait for the route: /api/t reaches the same
    // Mixpanel project from either server.
    const mixPanelMainUrl = getUnversionedRelayOrigin() + '/api/t/';
    const mixPanelToken = AppEnvironment.mixPanelConfig.mixpanel_token;
    let bodyData = { ...data };
    let callUrl = '';

    // if this is event
    if ('properties' in data) {
      // token is inside properties
      bodyData['properties'] = { ...data.properties };
      bodyData['properties']['token'] = mixPanelToken;
      callUrl = mixPanelMainUrl + 'track/';
      // if it is user update
    } else {
      // token is inside main data
      bodyData['$token'] = mixPanelToken;
      // if this is a known user-update type, it goes to the one shared engage
      // endpoint — the payload key ($set / $set_once / $add / $union) selects the
      // operation on Mixpanel's side. NEVER append the "#profile-set"-style doc
      // anchors from Mixpanel's API reference to this URL: browsers strip a '#'
      // fragment, but the iOS-native HTTP stack percent-encodes it into the path
      // (engage%23profile-set), which Mixpanel rejects with 400 — that silently
      // broke every iOS profile update in June 2026 (see design-docs/telemetry.md)
      if ('$set' in bodyData || '$set_once' in bodyData || '$add' in bodyData || '$union' in bodyData) {
        callUrl = mixPanelMainUrl + 'engage';
      } else {
        // an unknown update type means a producer bug — report it so it reaches
        // a future debugger, then drop the record (retrying can never succeed)
        this.logging.trackSilentProblem('unknownUserUpdateType', { keys: Object.keys(bodyData).join(',') });
        console.error('Unknown user update type:', bodyData);
        return true; // just remove it from the queue
      }
    }

    console.log('Sending to MixPanel:', bodyData);
    try {
      let response = await this.postEncodedRequest(callUrl /*+ "?verbose=1" */, bodyData); // add verbose if debugging
      if (response.status === 200 && response.result === '1') {
        return true;
      }
    } catch (err) {
      // #4762: isTimeoutError comes FIRST and is not optional. The send is now bounded by
      // createTimeoutSignal, which aborts with a DOMException named 'TimeoutError' — and
      // isTransientFetchError only recognises 'AbortError', so on its own it would answer
      // false and send every timed-out send down the console.error branch below. That would
      // be wrong twice over: a send we deliberately gave up on is the most ordinary
      // transient failure there is, and the E2E browser-console gate treats a console.error
      // as a test failure, so a slow network in CI would start failing nightly runs.
      if (isTimeoutError(err) || isTransientFetchError(err)) {
        console.log('MixPanel send failed (transient):', (err as any)?.message || err);
      } else {
        console.error('Error sending to MixPanel:', err);
      }
    }
    return false;
  }
}
